/** Convert rich-text HTML from the legal editor back to stored markdown. */

import { formatAlignPrefix, type TextAlign } from "@heylife/core/markdown";

/** Read text-align from rich-text HTML (contenteditable / execCommand). */
function readTextAlign(el: HTMLElement): TextAlign | undefined {
  const inline = el.style.textAlign?.toLowerCase();
  if (inline === "center" || inline === "right" || inline === "left") return inline;
  const attr = el.getAttribute("align")?.toLowerCase();
  if (attr === "center" || attr === "right") return attr;
  return undefined;
}

function inlineFromNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const el = node as HTMLElement;
  const inner = Array.from(el.childNodes).map(inlineFromNode).join("");
  const tag = el.tagName.toLowerCase();
  if (tag === "strong" || tag === "b") return `**${inner}**`;
  if (tag === "em" || tag === "i") return `_${inner}_`;
  if (tag === "code") return `\`${inner}\``;
  if (tag === "br") return "\n";
  return inner;
}

function headingMarkdown(tag: string, el: HTMLElement): string[] {
  const t = inlineFromNode(el).trim();
  if (!t) return [];
  const align = readTextAlign(el);
  const level = tag === "h2" ? "#" : tag === "h3" ? "##" : tag === "h4" ? "###" : tag === "h1" ? "#" : "##";
  return [`${level} ${formatAlignPrefix(align)}${t}`];
}

function paragraphMarkdown(el: HTMLElement): string[] {
  const t = inlineFromNode(el).trim();
  if (!t) return [];
  const align = readTextAlign(el);
  if (!align || align === "left") return [t];
  return [`{align:${align}} ${t}`];
}

function blockFromNode(node: Node): string[] {
  if (node.nodeType === Node.TEXT_NODE) {
    const t = (node.textContent ?? "").trim();
    return t ? [t] : [];
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return [];
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (tag === "ul" || tag === "ol") {
    return Array.from(el.children)
      .filter((c) => c.tagName.toLowerCase() === "li")
      .map((li) => `- ${inlineFromNode(li).trim()}`)
      .filter(Boolean);
  }
  if (tag === "h1" || tag === "h2" || tag === "h3" || tag === "h4" || tag === "h5" || tag === "h6") {
    return headingMarkdown(tag, el);
  }
  if (tag === "p" || tag === "div") {
    return paragraphMarkdown(el);
  }
  if (tag === "br") return [""];
  return Array.from(el.childNodes).flatMap(blockFromNode);
}

export function htmlToMarkdown(html: string): string {
  const root = document.createElement("div");
  root.innerHTML = html.trim() || "";
  const lines = Array.from(root.childNodes).flatMap(blockFromNode);
  return lines
    .join("\n")
    .replace(/\u00a0/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
